import { useState, useEffect } from 'react';
import { supabase } from './supabase';

export const useLoans = (userEmail) => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    const organization = userEmail.split('@')[1];
    
    const loadLoans = async () => {
      try {
        setLoading(true);
        
        // Get organization first
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('id')
          .eq('domain', organization)
          .single();

        if (orgError || !orgData) {
          console.error('Organization not found:', orgError);
          setLoans([]);
          setLoading(false);
          return;
        }

        // Get loans
        const { data: loansData, error } = await supabase
          .from('loans')
          .select('*')
          .eq('organization_id', orgData.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading loans:', error);
          setLoans([]);
        } else {
          setLoans(loansData || []);
        }
      } catch (error) {
        console.error('Error in useLoans:', error);
        setLoans([]);
      } finally {
        setLoading(false);
      }
    };

    loadLoans();
  }, [userEmail]);

  const addLoan = async (loanData, userEmail) => {
    try {
      const organization = userEmail.split('@')[1];

      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id')
        .eq('domain', organization)
        .single();

      if (orgError) {
        throw new Error(`Error buscando organización: ${orgError.message}`);
      }

      if (!orgData) {
        throw new Error('Organización no encontrada. Dominio: ' + organization);
      }

      const loanWithOrg = {
        ...loanData,
        organization_id: orgData.id
      };

      const { data, error } = await supabase
        .from('loans')
        .insert([loanWithOrg])
        .select();

      if (error) {
        throw new Error(`Error insertando préstamo: ${error.message}`);
      }

      // Refresh loans list
      setLoans(prevLoans => [data[0], ...prevLoans]);

      return data[0];
    } catch (error) {
      console.error('Error in addLoan:', error);
      throw error;
    }
  };

  const updateLoan = async (loanId, updates) => {
    try {
      const { data, error } = await supabase
        .from('loans')
        .update(updates)
        .eq('id', loanId)
        .select();

      if (error) throw error;
      
      // Update local state
      setLoans(prevLoans => 
        prevLoans.map(loan => 
          loan.id === loanId ? { ...loan, ...updates } : loan
        )
      );
      
      return data[0];
    } catch (error) {
      console.error('Error updating loan:', error);
      throw error;
    }
  };

  return { loans, loading, addLoan, updateLoan };
};

export const usePayments = (userEmail) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    const organization = userEmail.split('@')[1];
    
    const loadPayments = async () => {
      try {
        setLoading(true);
        
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('id')
          .eq('domain', organization)
          .single();

        if (orgError || !orgData) {
          console.error('Organization not found:', orgError);
          setPayments([]);
          setLoading(false);
          return;
        }

        // Get all loans for this org first
        const { data: loansData } = await supabase
          .from('loans')
          .select('id')
          .eq('organization_id', orgData.id);

        if (!loansData || loansData.length === 0) {
          setPayments([]);
          setLoading(false);
          return;
        }

        const loanIds = loansData.map(l => l.id);

        // Get payments for these loans
        const { data: paymentsData, error } = await supabase
          .from('payments')
          .select('*')
          .in('loan_id', loanIds)
          .order('payment_date', { ascending: false });

        if (error) {
          console.error('Error loading payments:', error);
          setPayments([]);
        } else {
          setPayments(paymentsData || []);
        }
      } catch (error) {
        console.error('Error in usePayments:', error);
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };

    loadPayments();
  }, [userEmail]);

  const addPayment = async (paymentData, userEmail) => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .insert([paymentData])
        .select();

      if (error) throw error;
      
      // Update local state
      setPayments(prevPayments => [data[0], ...prevPayments]);
      
      return data[0];
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  };

  return { payments, loading, addPayment };
};